import type { Email, ProviderSetupBranding } from "@src/types/api";

type ProviderSummary = NonNullable<Email["providerSetup"]>["tms"];

const GREENSCREENS_LOGO = `data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAFAAAAAzCAYAAAAAcY9ZAAAFfklEQVR42u2bS2wbVRSGv3Nn7PgVl5aG8liwQlDBDgQLBI3TrliUh5qACFRlAV2xRAgkZI/EokVlVyRSIbVSCEVuQYKiIgGqjcSCBSxYwBKxqcozje0kfmTmHhbjWCmQxHYd1bRzVok9Hl9/899zz/nvDERxHYaqALLu/1FsCi+KviKfNwAUi0lmv812Xp8r7QzfUxk2wGZ44KnB8yzHSxn8XeeQxp1rVLmXuyfmQBQYKohmaJTnieV4McMO5zwjib0QVDvvW6lw8y3PMFc+gYgdJojuUOQ8Ect734ySsp+RSj9CbaGBiTudY0QdKpctqcyLzJVjiLzQ/rC0VXnDAhQKBeHT75JUa+dIjT5KtRIg8u9xGcewWG2SvekQsxcMvhzmF1p46I07hfN5wfMs1coYsIfFmsWIswFvl+pCQCp9ECO78cSiaqIc6LgWdBkR04VmDa2GjRaRqxqLmAjgdRIRwAhgBDACGAGMYsg6EdWww/A8BV3fyvvpJwEVWl8JjtPr0B1KJZcffzSUSpvXhblcAIPvWtwtMQbChl9XK99148yZAAQyX69QD3r7Hl/nyeX8nlvHAUMcLMBi0WFqKuDkZ7eS2HYK1TGCQJF1KIZGglIP4iBxVBURQTf6jSJYC3H7BrOl39q9iWIB1CJrzAVrIRYTsBU+/v0YZ6aC0Pnx7PABXIU38/ltJDJfkErfR7MOsVg3Ux5aTbpWhyqkM4cwTnfHxkfgCXmQyeIUk5MWGBhEd2DTdmoqYObTnWQz53Hd+6gu1EGcHmaMwYgS6tUnFlujJFUUHyFgVZ5LSwGi3Z18EUt69Cnqu04jMgVoR/3XHGA+bygUlHtyu8F8yfadd7BYgUw22df5RMD3XZaDNX6giTOadTHiYkx/Yw982HX7JB+Uy6geoFCYR5WrhXj1AAsFpVAQ7trzGCOJr/n94hLGuH2lahENCdqAhFtZs+D+TPXyKZrNAPq0rwS4tLxCIrWNxuI4nncWCmYrVuYoerwug6v9Tnw/uEXp8P1+x64f+LkfWBm+C1EsOv8b2czMxBiaMmZ1L7c59jpnvz1Afan6n3sa3V2HsA60GhAET3No30UATl54mHTqbVr1/nNguND7jIyM0mocAT5EdbXoH4JFpNF8B8dMMHbbOAvz4PbLUMD3odlKrilwdpJKPQSWNatwD+kFsAFs2wF/XHqX5exHg4A3GIAi2i4H5pmc3MeTL39EIvk4S9VG19a7IKiYdkkhqPrhPkmHwAqNehCuwrRThQZdj09ESWUS/PXrWzw/8eqgasDBFdIi2q4HLSdOTCK7z7J9bD/NRqiobmq0Rj3UStjKXfnjjEgHnOBgDCRTTlfKA3BjUJs/xvTEqxTVQRjCVi5sjQyet8LMzAF09wHAIdhIKOqABKCPkEi+RKNhEZwNUqRiHAiCGsuL5zbFIGqJJx3qtR+YnjgatpvYQW7GD9ZM8DwLKhyWFeB015+bLTWIxV6iUddNKivFjYHvX2R6YrqPOyACGHo/UBRUyJedLr/fxzCK9iQKh5K6lMsbH3XvePukZ9gKeFt4a4coHpt7dfkSeDmf2Qu255w7TkAup1vh8UWWfgQwAhgBjCICGAGMAHZflgj00l5pdH/gP/qLJrG42RSioqCKSCYCCODlAvJqWK59wsLl02RviqPWX3d7UgRiI4LleNsFuuZ7Gu41114BkP3LwLO8X7Zkt09TmW9ccZRVRbAk03EWK69xcO9R8oPx8/7/UzhUkpBXw3Pjz1GrzpLOJmj59go7Kz1qWKq9wvN7j1AquXhio+dE/mnKkjdM7znIXLmFw5o8Z+L8eelNDu47Rqnk9nFPDDfeg4bnz490/j5ZSnQeB4uiq61WiZ7ijCKKbuJvGY87+WOy4dMAAAAASUVORK5CYII=`;

const CLIENT_BRANDING_FALLBACKS: Record<
  string,
  { logoSrc: string; logoAlt: string; logoHeight: number }
> = {
  greenscreens: { logoSrc: GREENSCREENS_LOGO, logoAlt: "GreenScreens", logoHeight: 40 },
};

export type ProviderBranding = {
  logoSrc?: string;
  logoAlt: string;
  logoHeight: number;
};

function brandingFromServer(b?: ProviderSetupBranding | null): ProviderBranding | null {
  if (!b || typeof b.logoAlt !== "string" || typeof b.logoHeight !== "number") return null;
  return {
    logoSrc: b.logoSrc,
    logoAlt: b.logoAlt,
    logoHeight: b.logoHeight,
  };
}

export function getTmsBranding(
  tmsProvider: ProviderSummary,
  fallbackName?: string
): ProviderBranding {
  const fromServer = brandingFromServer(tmsProvider?.branding);

  if (fromServer) return fromServer;
  const label = fallbackName || tmsProvider?.name || "TMS";

  return { logoAlt: label, logoHeight: 18 };
}
export function getRatingEngineBranding(
  displayName: string,
  serverBranding?: ProviderSetupBranding | null,
  providerKey?: string
): ProviderBranding {
  const fromServer = brandingFromServer(serverBranding);

  if (fromServer) return fromServer;
  const key = (providerKey ?? displayName).trim().toLowerCase();
  const staticFallback = CLIENT_BRANDING_FALLBACKS[key];

  if (staticFallback) return staticFallback;
  return { logoAlt: displayName, logoHeight: 16 };
}
